import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import BreadCrumb from '../../../../components/BreadCrumb/BreadCrumb';
import axiosWrapper from '../../../../utils/api';
import { API_URL } from '../../../../utils/apiUrl';
import '../Groups.scss';

// Utility to format values properly
const formatValue = (value, type = 'string') => {
    // If value is null, undefined, or if it's a number and is NaN, return a default placeholder.
    if (value === undefined || value === null || (typeof value === 'number' && isNaN(value))) {
        return '-';
    }
    // Format booleans as On/Off.
    if (typeof value === 'boolean') {
        return value ? 'On' : 'Off';
    }
    // If the type is "currency", prepend a "$"
    if (type === 'currency') {
        return `$${value}`;
    }
    if (type === 'percentage') {
        return `${value}%`;
    }
    return value;
};

const DataRow = ({ label, value, type }) => (
    <div className="data-row">
        <div className="label">{label}</div>
        <div className="value">{formatValue(value, type)}</div>
    </div>
);

const GroupDetails = () => {
    const [groupData, setGroupData] = useState(null);
    const { groupId } = useParams();
    const { userToken } = useSelector((state) => state.auth);

    const getGroupAdminById = async () => {
        try {
            const response = await axiosWrapper('get', API_URL.GET_GROUP_ADMIN_BY_ID(groupId), {}, userToken);
            if (response?.data) {
                setGroupData(response.data);
            }
        } catch (error) {
            setGroupData(null);
        }
    };

    useEffect(() => {
        if (groupId) {
            getGroupAdminById();
        }
    }, [groupId, userToken]);

    if (!groupData) {
        return <div className="loading">Loading...</div>;
    }

    return (
        <>
            <Helmet>
                <title>Group Details | Level Parking</title>
            </Helmet>

            <BreadCrumb items={[{ label: 'Groups', href: '/admin/groups' }, { label: 'Group Details' }]} />

            <div className="group-detailss-container">
                <div className="header my-3 mb-5">
                    <h1 className="main-heading">Information</h1>
                    <Link to={`/admin/view-group-details/${groupId}`} className="view-parkers-btn">
                        View Parkers
                    </Link>
                </div>

                <div className="content-section">
                    <div className="details-heading">General Info</div>
                    <div className="info-grid">
                        <DataRow label="Group Name" value={groupData.groupName} />
                        <DataRow label="Street Address 1" value={groupData.streetAddress} />
                        <DataRow label="Street Address 2" value={groupData.streetAddress2} />
                        <DataRow label="City" value={groupData.city} />
                        <DataRow label="State" value={groupData.state} />
                        <DataRow label="Zip Code" value={groupData.zipCode} />
                    </div>

                    <div className="details-heading">Primary Contact</div>
                    <div className="info-grid">
                        <DataRow label="First Name" value={groupData.firstName} />
                        <DataRow label="Last Name" value={groupData.lastName} />
                        <DataRow label="Title" value={groupData.title} />
                        <DataRow label="Email Address" value={groupData.email} />
                        <DataRow label="Phone Number" value={groupData.phoneNumber} />
                        <DataRow label="Notifications" value={groupData.notifications} />
                    </div>
                </div>

                <h1 className="main-heading my-4">Rates</h1>
                <div className="content-section">
                    <div className="details-heading">Subscriptions</div>
                    <div className="info-grid">
                        {groupData.subscriptions && groupData.subscriptions.length > 0 ? (
                            groupData.subscriptions.map((sub, index) => (
                                <div key={index} className="subscription-block">
                                    {/* Display zone from the full location document */}
                                    <DataRow label="Zone" value={sub.location?.zone} />
                                    {/* Display subscription plan name */}
                                    <DataRow label="Subscription" value={sub.name} />
                                    <DataRow label="Special Rate" value={sub.metadata?.specialRate} type="currency" />
                                    <DataRow label="Parker Minimum" value={sub.metadata?.parkerMinimum} />
                                    <DataRow label="Parker Maximum" value={sub.metadata?.parkerMaximum} />
                                    <DataRow label="Service Fee" value={sub.metadata?.serviceFee} type="currency" />
                                </div>
                            ))
                        ) : (
                            <div className="no-data">No subscriptions available</div>
                        )}
                    </div>

                    <div className="details-heading">Validations</div>
                    <div className="info-grid">
                        {groupData.validations && groupData.validations.length > 0 ? (
                            groupData.validations.map((validation, index) => (
                                <div key={index} className="validation-block">
                                    <DataRow label="Zone" value={validation.zone} />
                                    <DataRow
                                        label="Discount"
                                        value={validation.discount}
                                        type={validation.isFixedPrice ? 'currency' : 'percentage'}
                                    />
                                    <DataRow label="Maximum Duration" value={validation.maximumDuration} />
                                    <DataRow label="Fixed Price" value={validation.isFixedPrice} />
                                    <DataRow label="Monthly Maximum" value={validation.monthlyMaximum} />
                                </div>
                            ))
                        ) : (
                            <div className="no-data">No validations available</div>
                        )}
                    </div>
                </div>

                <h1 className="main-heading my-4">Billing</h1>
                <div className="content-section">
                    <div className="details-heading">Terms</div>
                    <div className="info-grid">
                        <DataRow label="Terms" value={groupData.terms?.invoicing ? 'Invoicing' : 'AutoPay'} />
                        {groupData.terms?.invoicing && (
                            <>
                                <DataRow label="Net Terms" value={groupData.terms?.netTerms} />
                                <DataRow
                                    label="Processing Fee"
                                    value={groupData.terms?.processingFee}
                                    type="percentage"
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default GroupDetails;
